import * as React from 'react';
import { Button, Card, Col, Dropdown, Empty, Row, Spin } from 'antd';
import { Column, Line } from '@ant-design/plots';
import { CaretDownOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useCampaignQuery, useCampaignSummary } from '../queryHooks';
import { formatCompact, formatEuro, formatPct, formatVIT, PLATFORM_LABEL } from '../format';
import KpiStrip, { Kpi } from '../components/KpiStrip';
import type { DailyPoint } from '../types';

dayjs.extend(isoWeek);

type ChartMetric =
    | 'vit'
    | 'posts'
    | 'engagements'
    | 'engagement_rate'
    | 'views'
    | 'organic_views'
    | 'paid_views'
    | 'impressions'
    | 'reach';
type ChartGrain = 'day' | 'week' | 'month';

const CHART_METRICS: { key: ChartMetric; label: string }[] = [
    { key: 'vit', label: 'VIT' },
    { key: 'posts', label: 'Posts' },
    { key: 'engagements', label: 'Engagements' },
    { key: 'engagement_rate', label: 'Engagement rate' },
    { key: 'views', label: 'Video views' },
    { key: 'organic_views', label: 'Organic video views' },
    { key: 'paid_views', label: 'Paid video views' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'reach', label: 'Reach' },
];

const CHART_GRAINS: { key: ChartGrain; label: string }[] = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
];

function emptyPoint(date: string): DailyPoint {
    return {
        date, posts: 0, vit: 0, engagements: 0, views: 0,
        organic_video_views: 0, paid_video_views: 0, impressions: 0, reach: 0, er_sum: 0, er_count: 0,
    };
}

function bucketKey(date: string, grain: ChartGrain): string {
    const d = dayjs(date);
    if (grain === 'month') return d.startOf('month').format('YYYY-MM-DD');
    if (grain === 'week') return d.startOf('isoWeek').format('YYYY-MM-DD');
    return d.format('YYYY-MM-DD');
}

function addPoint(buckets: Map<string, DailyPoint>, point: DailyPoint, grain: ChartGrain) {
    const key = bucketKey(point.date, grain);
    const acc = buckets.get(key) ?? emptyPoint(key);
    acc.posts += point.posts;
    acc.vit += point.vit;
    acc.engagements += point.engagements;
    acc.views += point.views;
    acc.organic_video_views = (acc.organic_video_views ?? 0) + (point.organic_video_views ?? 0);
    acc.paid_video_views = (acc.paid_video_views ?? 0) + (point.paid_video_views ?? 0);
    acc.impressions = (acc.impressions ?? 0) + (point.impressions ?? 0);
    acc.reach = (acc.reach ?? 0) + (point.reach ?? 0);
    acc.er_sum = (acc.er_sum ?? 0) + (point.er_sum ?? 0);
    acc.er_count = (acc.er_count ?? 0) + (point.er_count ?? 0);
    buckets.set(key, acc);
}

function fillCampaignDays(buckets: Map<string, DailyPoint>, grain: ChartGrain, start?: string | null, end?: string | null, points: DailyPoint[] = []) {
    let from = start ? dayjs(start).startOf('day') : null;
    let to = end ? dayjs(end).startOf('day') : null;
    if ((!from || !to) && points.length > 0) {
        const dates = points.map((p) => dayjs(p.date));
        from = from ?? dates.reduce((a, b) => (a.isBefore(b) ? a : b));
        to = to ?? dates.reduce((a, b) => (a.isAfter(b) ? a : b));
    }
    if (!from || !to) return;
    if (to.isBefore(from)) [from, to] = [to, from];
    const step = grain === 'month' ? 'month' : grain === 'week' ? 'week' : 'day';
    let cursor = grain === 'month' ? from.startOf('month') : grain === 'week' ? from.startOf('isoWeek') : from.startOf('day');
    const last = grain === 'month' ? to.startOf('month') : grain === 'week' ? to.startOf('isoWeek') : to.startOf('day');
    while (!cursor.isAfter(last)) {
        const key = cursor.format('YYYY-MM-DD');
        if (!buckets.has(key)) buckets.set(key, emptyPoint(key));
        cursor = cursor.add(1, step);
    }
}

export function dayAxisLabel(iso: string): string {
    const d = new Date(`${iso}T00:00:00`);
    const dayMonth = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    return `${dayMonth}\n${d.getFullYear()}`;
}

export function dailyChartSeries(
    points: DailyPoint[] | undefined,
    metric: 'posts' | 'vit',
    start?: string | null,
    end?: string | null,
) {
    return bucketDaily(points ?? [], metric, 'day', start, end);
}

function bucketDaily(points: DailyPoint[], metric: ChartMetric, grain: ChartGrain, start?: string | null, end?: string | null) {
    const buckets = new Map<string, DailyPoint>();
    for (const point of points) addPoint(buckets, point, grain);
    fillCampaignDays(buckets, grain, start, end, points);
    return [...buckets.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, acc]) => ({
            date: key,
            value: metricValue(acc, metric),
        }));
}

function metricValue(point: DailyPoint, metric: ChartMetric): number {
    switch (metric) {
        case 'vit': return point.vit;
        case 'posts': return point.posts;
        case 'engagements': return point.engagements;
        case 'views': return point.views;
        case 'organic_views': return point.organic_video_views ?? 0;
        case 'paid_views': return point.paid_video_views ?? 0;
        case 'impressions': return point.impressions ?? 0;
        case 'reach': return point.reach ?? 0;
        case 'engagement_rate':
            return (point.er_count ?? 0) > 0 ? ((point.er_sum ?? 0) / (point.er_count ?? 1)) * 100 : 0;
    }
}

const optionHeading: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: 'var(--text-muted)',
    padding: '0 10px 8px',
};

function optionRow(selected: boolean): React.CSSProperties {
    return {
        width: 'fit-content',
        margin: '2px 4px',
        padding: '6px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        fontWeight: selected ? 600 : 400,
        background: selected ? 'var(--surface-border)' : 'transparent',
        color: 'var(--text-strong)',
    };
}

const SummarySection: React.FC<{
    data?: ReturnType<typeof useCampaignSummary>['data'];
    loading?: boolean;
    skipFetch?: boolean;
    /** When set, the chart fills this window instead of the campaign dates. */
    period?: { start?: string | null; end?: string | null };
}> = ({ data: injected, loading, skipFetch, period }) => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const controlled = skipFetch || loading !== undefined || injected !== undefined;
    const q = useCampaignSummary(controlled ? undefined : campaignId);
    const campaignQuery = useCampaignQuery(campaignId);
    const data = injected ?? q.data;
    const isLoading = loading ?? q.isLoading;
    const [metric, setMetric] = React.useState<ChartMetric>('vit');
    const [grain, setGrain] = React.useState<ChartGrain>('day');

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
    if (!data) return <Empty />;

    const chartData = bucketDaily(
        data.daily ?? [],
        metric,
        grain,
        period ? period.start : campaignQuery.data?.start_date,
        period ? period.end : campaignQuery.data?.end_date,
    );
    const metricLabel = CHART_METRICS.find((m) => m.key === metric)?.label ?? 'VIT';

    const kpis: { label: string; value: string }[] = [
        { label: 'ENGAGEMENTS', value: formatCompact(data.engagements) },
        { label: 'ENGAGEMENT RATE', value: formatPct(data.engagement_rate) },
        { label: 'VIDEO VIEWS', value: formatCompact(data.video_views) },
        { label: 'ORGANIC VIDEO VIEWS', value: formatCompact(data.organic_video_views) },
        { label: 'PAID VIDEO VIEWS', value: formatCompact(data.paid_video_views) },
        { label: 'IMPRESSIONS', value: formatCompact(data.impressions) },
        { label: 'REACH', value: formatCompact(data.reach) },
        { label: 'EST. IMPRESSIONS', value: formatCompact(data.est_impressions) },
        { label: 'POTENTIAL REACH', value: formatCompact(data.potential_reach) },
        { label: 'TOTAL SPENT', value: formatEuro(data.total_spent) },
        { label: 'COST PER POST', value: formatEuro(data.cost_per_post) },
        { label: 'COST PER VIDEO', value: formatEuro(data.cost_per_video) },
        { label: 'COST PER ENGAGEMENT', value: formatEuro(data.cost_per_engagement) },
        { label: 'COST PER VIDEO VIEW', value: formatEuro(data.cost_per_video_view) },
        { label: 'CLICKS', value: formatCompact(data.clicks) },
        { label: 'CONVERSIONS', value: formatCompact(data.conversions) },
        { label: 'REVENUE', value: formatEuro(data.revenue) },
    ];

    const platformCols = data.platforms.map((p) => ({
        name: PLATFORM_LABEL[p.name] || p.name,
        value: p.value,
    }));
    const tierCols = data.tiers.filter((t) => t.value > 0).map((p) => ({
        name: p.name.charAt(0).toUpperCase() + p.name.slice(1),
        value: p.value,
    }));

    return (
        <div>
            <KpiStrip items={[
                { label: 'RESPONDENTS', value: data.respondents },
                { label: 'POSTS', value: data.posts },
                { label: 'VIT', value: formatVIT(data.vit) },
                { label: 'RESPONSE RATE', value: formatPct(data.response_rate) },
            ]} />

            <Card
                size="small"
                title="PERFORMANCE OVER TIME"
                extra={
                    <Dropdown
                        trigger={['click']}
                        dropdownRender={() => (
                            <div style={{
                                display: 'flex',
                                background: 'var(--surface)',
                                borderRadius: 8,
                                boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                                border: '1px solid var(--surface-border)',
                                overflow: 'hidden',
                            }}>
                                <div style={{ padding: '12px 8px', minWidth: 210, maxHeight: 320, overflowY: 'auto', borderRight: '1px solid var(--surface-border)' }}>
                                    <div style={optionHeading}>METRIC TO MEASURE</div>
                                    {CHART_METRICS.map((item) => (
                                        <div
                                            key={item.key}
                                            onClick={() => setMetric(item.key)}
                                            style={optionRow(item.key === metric)}
                                        >
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                                <div style={{ padding: '12px 8px', minWidth: 140 }}>
                                    <div style={optionHeading}>VIEW CHART BY</div>
                                    {CHART_GRAINS.map((item) => (
                                        <div
                                            key={item.key}
                                            onClick={() => setGrain(item.key)}
                                            style={optionRow(item.key === grain)}
                                        >
                                            {item.label}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        <Button size="small">
                            {metricLabel} by {grain} <CaretDownOutlined />
                        </Button>
                    </Dropdown>
                }
                style={{ marginBottom: 16 }}
            >
                {chartData.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dated posts yet" />
                ) : (
                    <Line
                        data={chartData}
                        xField="date"
                        yField="value"
                        height={240}
                        autoFit={false}
                        smooth={false}
                        color="#722ed1"
                        point={{ size: 0, style: { opacity: 0 } }}
                        yAxis={{ min: 0 }}
                        xAxis={{
                            tickCount: 9,
                            label: {
                                autoHide: true,
                                autoRotate: false,
                                formatter: (value: string) => grain === 'month'
                                    ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
                                    : dayAxisLabel(value),
                            },
                        }}
                    />
                )}
            </Card>

            <Card size="small" title="KPIS" style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 16 }}>
                    {kpis.map((k) => (
                        <Kpi key={k.label} label={k.label} value={k.value} />
                    ))}
                </div>
            </Card>

            <Row gutter={16}>
                <Col span={12}>
                    <Card size="small" title="PLATFORMS">
                        {platformCols.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                            <Column data={platformCols} xField="name" yField="value" height={180} autoFit={false} color="#722ed1" />
                        )}
                    </Card>
                </Col>
                <Col span={12}>
                    <Card size="small" title="TIERS">
                        {tierCols.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
                            <Column data={tierCols} xField="name" yField="value" height={180} autoFit={false} color="#722ed1" />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SummarySection;
